import { reporter } from 'modules/analytics/constants';
import type { AnalyticEventName } from 'modules/analytics/analytics';
import type {
  InsertedMacroDataSource,
  InsertedPickListDataSource,
  InsertedTemplateDataSource,
} from 'domains/reporter/Reporter/state';
import { performance, createMarkForId } from 'utils/performance';
import { wordCount } from '../plugins/dictation/utils';
import type { FocusModeMacroResolutionStatus } from '../plugins/dictation/focusMode/focusModeTelemetry';
import { atom, RecoilState } from 'recoil';
import type { Json } from 'types';

export const marks = Object.freeze({
  nvoq: {
    Dictation: {
      DoneMessageSent: 'nvoq.Dictation.DoneMessageSent',
      FirstNonEmptyHypothesisText: 'nvoq.Dictation.FirstNonEmptyHypothesisText',
      FirstNonEmptyStableText: 'nvoq.Dictation.FirstNonEmptyStableText',
      StableTextReceived: 'nvoq.Dictation.StableTextReceived',
      StartDictation: 'nvoq.Dictation.StartDictation',
    },
    WS: {
      Create: 'nvoq.WS.Create',
      Open: 'nvoq.WS.Open',
      Close: 'nvoq.WS.Close',
    },
  },
  sirona: {
    Dictation: {
      RecordingStarted: 'sirona.Dictation.RecordingStarted',
      RecordingStopped: 'sirona.Dictation.RecordingStopped',
      RecordingStart: {
        Requested: 'sirona.Dictation.RecordingStart.Requested',
        RecorderStarted: 'sirona.Dictation.RecordingStart.RecorderStarted',
        UiPainted: 'sirona.Dictation.RecordingStart.UiPainted',
      },
      RecordingStop: {
        Requested: 'sirona.Dictation.RecordingStop.Requested',
        RecorderStopped: 'sirona.Dictation.RecordingStop.RecorderStopped',
        UiPainted: 'sirona.Dictation.RecordingStop.UiPainted',
      },
    },
    AudioCue: {
      StartRecording: {
        Requested: 'sirona.AudioCue.StartRecording.Requested',
        Playing: 'sirona.AudioCue.StartRecording.Playing',
      },
      StopRecording: {
        Requested: 'sirona.AudioCue.StopRecording.Requested',
        Playing: 'sirona.AudioCue.StopRecording.Playing',
      },
    },
    ReportExitAction: {
      Start: 'sirona.ReportExitAction.Start',
      End: 'sirona.ReportExitAction.End',
    },
    AutoImpressionRender: {
      Start: 'sirona.AutoImpressionRender.Start',
      End: 'sirona.AutoImpressionRender.End',
    },
    InsertTemplate: {
      Start: 'sirona.InsertTemplate.Start',
      FetchData: 'sirona.InsertTemplate.FetchData',
      EditDocument: 'sirona.InsertTemplate.EditDocument',
      ScreenUpdate: 'sirona.InsertTemplate.ScreenUpdate',
    },
    InsertMacro: {
      Start: 'sirona.InsertMacro.Start',
      FetchData: 'sirona.InsertMacro.FetchData',
      EditDocument: 'sirona.InsertMacro.EditDocument',
      ScreenUpdate: 'sirona.InsertMacro.ScreenUpdate',
    },
    // Picklist options are already resident on the client, so there is no fetch stage.
    InsertPickList: {
      Start: 'sirona.InsertPickList.Start',
      EditDocument: 'sirona.InsertPickList.EditDocument',
      ScreenUpdate: 'sirona.InsertPickList.ScreenUpdate',
    },
    ReportNavigation: {
      Start: 'sirona.ReportNavigation.Start',
      End: 'sirona.ReportNavigation.End',
    },
    AutoloadNextCase: {
      Start: 'sirona.AutoloadNextCase.Start',
      End: 'sirona.AutoloadNextCase.End',
    },
    RightDrawerTableSpinner: {
      Start: 'sirona.RightDrawerTableSpinner.Start',
    },
    RightDrawerTablePagination: {
      Start: 'sirona.RightDrawerTablePagination.Start',
    },
    FocusModeSession: {
      Start: 'sirona.FocusModeSession.Start',
    },
    FocusModeMacroResolution: {
      Start: 'sirona.FocusModeMacroResolution.Start',
    },
  },
});

export const measures = Object.freeze({
  nvoq: {
    Dictation: {
      HypothesisStableGap: {
        name: reporter.sys.nvoqHypothesisStableGap,
        startMark: marks.nvoq.Dictation.FirstNonEmptyHypothesisText,
        endMark: marks.nvoq.Dictation.FirstNonEmptyStableText,
      },
      TimeToFirstStableText: {
        name: reporter.sys.nvoqTimeToFirstStableText,
        startMark: marks.nvoq.Dictation.StartDictation,
        endMark: marks.nvoq.Dictation.StableTextReceived,
      },
      TimeToLastStableText: {
        name: reporter.sys.nvoqTimeToLastStableText,
        startMark: marks.nvoq.Dictation.DoneMessageSent,
        endMark: marks.nvoq.Dictation.StableTextReceived,
      },
    },
    WS: {
      TimeToConnect: {
        name: reporter.sys.nvoqWebSocketTimeToConnect,
        startMark: marks.nvoq.WS.Create,
        endMark: marks.nvoq.WS.Open,
      },
      TimeConnected: {
        name: reporter.sys.nvoqWebSocketTimeConnected,
        startMark: marks.nvoq.WS.Open,
        endMark: marks.nvoq.WS.Close,
      },
    },
  },
  sirona: {
    Dictation: {
      DictationDuration: {
        name: reporter.sys.dictationDuration,
        startMark: marks.sirona.Dictation.RecordingStarted,
        endMark: marks.sirona.Dictation.RecordingStopped,
      },
      RecordingStartRecorderStarted: {
        name: reporter.sys.recordingStartRecorderStartedLatency,
        startMark: marks.sirona.Dictation.RecordingStart.Requested,
        endMark: marks.sirona.Dictation.RecordingStart.RecorderStarted,
      },
      RecordingStartUiPainted: {
        name: reporter.sys.recordingStartUiPaintedLatency,
        startMark: marks.sirona.Dictation.RecordingStart.Requested,
        endMark: marks.sirona.Dictation.RecordingStart.UiPainted,
      },
      RecordingStopRecorderStopped: {
        name: reporter.sys.recordingStopRecorderStoppedLatency,
        startMark: marks.sirona.Dictation.RecordingStop.Requested,
        endMark: marks.sirona.Dictation.RecordingStop.RecorderStopped,
      },
      RecordingStopUiPainted: {
        name: reporter.sys.recordingStopUiPaintedLatency,
        startMark: marks.sirona.Dictation.RecordingStop.Requested,
        endMark: marks.sirona.Dictation.RecordingStop.UiPainted,
      },
    },
    AudioCue: {
      PlayStartLatency: {
        name: reporter.sys.audioCueStartLatency,
        startMark: marks.sirona.AudioCue.StartRecording.Requested,
        endMark: marks.sirona.AudioCue.StartRecording.Playing,
      },
      PlayStopLatency: {
        name: reporter.sys.audioCueStopLatency,
        startMark: marks.sirona.AudioCue.StopRecording.Requested,
        endMark: marks.sirona.AudioCue.StopRecording.Playing,
      },
    },
    // Stays flat, and keeps its event name, so its Datadog history is continuous and
    // `measures.sirona.InsertTemplate.name` still resolves for the analytics observer.
    // The boundary is unchanged; only the end mark key was renamed.
    InsertTemplate: {
      name: reporter.usr.insertTemplate,
      startMark: marks.sirona.InsertTemplate.Start,
      endMark: marks.sirona.InsertTemplate.EditDocument,
    },
    InsertTemplateStages: {
      TriggerToFetch: {
        name: reporter.usr.insertTemplateTriggerToFetch,
        startMark: marks.sirona.InsertTemplate.Start,
        endMark: marks.sirona.InsertTemplate.FetchData,
      },
      FetchToEdit: {
        name: reporter.usr.insertTemplateFetchToEdit,
        startMark: marks.sirona.InsertTemplate.FetchData,
        endMark: marks.sirona.InsertTemplate.EditDocument,
      },
      EditToScreen: {
        name: reporter.usr.insertTemplateEditToScreen,
        startMark: marks.sirona.InsertTemplate.EditDocument,
        endMark: marks.sirona.InsertTemplate.ScreenUpdate,
      },
      Total: {
        name: reporter.usr.insertTemplateTotal,
        startMark: marks.sirona.InsertTemplate.Start,
        endMark: marks.sirona.InsertTemplate.ScreenUpdate,
      },
    },
    InsertMacroStages: {
      TriggerToFetch: {
        name: reporter.usr.insertMacroTriggerToFetch,
        startMark: marks.sirona.InsertMacro.Start,
        endMark: marks.sirona.InsertMacro.FetchData,
      },
      FetchToEdit: {
        name: reporter.usr.insertMacroFetchToEdit,
        startMark: marks.sirona.InsertMacro.FetchData,
        endMark: marks.sirona.InsertMacro.EditDocument,
      },
      EditToScreen: {
        name: reporter.usr.insertMacroEditToScreen,
        startMark: marks.sirona.InsertMacro.EditDocument,
        endMark: marks.sirona.InsertMacro.ScreenUpdate,
      },
      Total: {
        name: reporter.usr.insertMacroTotal,
        startMark: marks.sirona.InsertMacro.Start,
        endMark: marks.sirona.InsertMacro.ScreenUpdate,
      },
    },
    InsertPickListStages: {
      TriggerToEdit: {
        name: reporter.usr.insertPickListTriggerToEdit,
        startMark: marks.sirona.InsertPickList.Start,
        endMark: marks.sirona.InsertPickList.EditDocument,
      },
      EditToScreen: {
        name: reporter.usr.insertPickListEditToScreen,
        startMark: marks.sirona.InsertPickList.EditDocument,
        endMark: marks.sirona.InsertPickList.ScreenUpdate,
      },
      Total: {
        name: reporter.usr.insertPickListTotal,
        startMark: marks.sirona.InsertPickList.Start,
        endMark: marks.sirona.InsertPickList.ScreenUpdate,
      },
    },
    ReportNavigation: {
      name: reporter.usr.reportNavigation,
      startMark: marks.sirona.ReportNavigation.Start,
      endMark: marks.sirona.ReportNavigation.End,
    },
    ReportExitAction: {
      name: reporter.usr.reportExitAction,
      startMark: marks.sirona.ReportExitAction.Start,
      endMark: marks.sirona.ReportExitAction.End,
    },
    AutoImpressionRender: {
      name: reporter.usr.autoImpressionRendered,
      startMark: marks.sirona.AutoImpressionRender.Start,
      endMark: marks.sirona.AutoImpressionRender.End,
    },
    AutoloadNextCase: {
      name: reporter.usr.autoloadNextCase,
      startMark: marks.sirona.AutoloadNextCase.Start,
      endMark: marks.sirona.AutoloadNextCase.End,
    },
    // Open-ended measures: `performance.measure` defaults the end to now, so there is no End mark.
    RightDrawerTableSpinner: {
      name: reporter.sys.rightDrawerTableSpinnerLong,
      startMark: marks.sirona.RightDrawerTableSpinner.Start,
    },
    RightDrawerTablePagination: {
      name: reporter.sys.rightDrawerTablePaginationLong,
      startMark: marks.sirona.RightDrawerTablePagination.Start,
    },
    FocusModeSession: {
      name: reporter.sys.focusModeSessionDuration,
      startMark: marks.sirona.FocusModeSession.Start,
    },
    FocusModeMacroResolution: {
      name: reporter.sys.focusModeMacroResolution,
      startMark: marks.sirona.FocusModeMacroResolution.Start,
    },
  },
});

/**
 * Builds a measure whose start mark belongs to a single in-flight episode. The mark is cleared
 * whenever an episode starts, ends, or is abandoned, so a leftover mark can never be attributed to
 * a later episode.
 */
const createSingletonMeasure = <TDetail>({
  name,
  startMark,
}: {
  name: AnalyticEventName;
  startMark: string;
}) => ({
  start: () => {
    performance.clearMarks(startMark);
    performance.mark(startMark);
  },
  measure: (detail?: TDetail) => {
    if (!performance.hasEntry(startMark)) {
      return;
    }

    performance.measure(name, { start: startMark, detail });
    performance.clearMarks(startMark);
  },
  cancel: () => {
    performance.clearMarks(startMark);
  },
});

/**
 * Same contract as `createSingletonMeasure`, but keyed by an id so concurrent episodes of the same
 * measure do not collide on one mark name.
 */
const createKeyedMeasure = <TDetail>({
  name,
  startMark,
}: {
  name: AnalyticEventName;
  startMark: string;
}) => ({
  start: (id: string) => {
    performance.clearMarks(createMarkForId(startMark, id));
    performance.mark(startMark, id);
  },
  measure: (id: string, detail?: TDetail) => {
    const mark = createMarkForId(startMark, id);
    if (!performance.hasEntry(mark)) {
      return;
    }

    performance.measure(name, { start: mark, detail });
    performance.clearMarks(mark);
  },
  cancel: (id: string) => {
    performance.clearMarks(createMarkForId(startMark, id));
  },
});

export const rightDrawerTableSpinnerMeasure = createSingletonMeasure<Json>({
  name: measures.sirona.RightDrawerTableSpinner.name,
  startMark: measures.sirona.RightDrawerTableSpinner.startMark,
});

export const rightDrawerTablePaginationMeasure = createSingletonMeasure<Json>({
  name: measures.sirona.RightDrawerTablePagination.name,
  startMark: measures.sirona.RightDrawerTablePagination.startMark,
});

export const focusModeSessionMeasure = createSingletonMeasure<never>({
  name: measures.sirona.FocusModeSession.name,
  startMark: measures.sirona.FocusModeSession.startMark,
});

export type FocusModeMacroResolutionDetail = {
  macro_smid: Nullable<string>;
  status: FocusModeMacroResolutionStatus;
};

export const focusModeMacroResolutionMeasure = createKeyedMeasure<FocusModeMacroResolutionDetail>({
  name: measures.sirona.FocusModeMacroResolution.name,
  startMark: measures.sirona.FocusModeMacroResolution.startMark,
});

// Each insertable type carries its own source enum, because the valid trigger actions
// genuinely differ per type — a shared enum would force every consumer to handle members
// that cannot occur for its type.
export type InsertTemplateStageMetadata = {
  templateId: string;
  source: InsertedTemplateDataSource;
  wasTemplateResident: boolean;
};

export type InsertMacroStageMetadata = {
  macroId: string;
  source: InsertedMacroDataSource;
};

export type InsertPickListStageMetadata = {
  pickListId: string;
  source: InsertedPickListDataSource;
};

/**
 * Travels from the trigger site to the insertion handler. The id keys every mark for one
 * insertion so concurrent insertions cannot collide; the metadata is what the staged
 * measures carry as `detail`.
 */
export type InsertionContext<TMetadata> = TMetadata & {
  insertionId: string;
};

export type TemplateInsertionContext = InsertionContext<InsertTemplateStageMetadata>;
export type MacroInsertionContext = InsertionContext<InsertMacroStageMetadata>;
export type PickListInsertionContext = InsertionContext<InsertPickListStageMetadata>;

/**
 * The Start mark carries no detail, so starting an insertion needs only its key. Autoload
 * relies on this: it marks Start at the trigger, before the template to insert has been
 * resolved from the modality and procedure codes.
 */
export type InsertionStart = {
  insertionId: string;
};

/** A started template insertion, before the template to insert has been resolved. */
export type TemplateInsertionStart = InsertionStart & {
  source: InsertedTemplateDataSource;
};

export type InsertionMeasure = {
  name: AnalyticEventName;
  startMark: string;
  endMark: string;
};

type FetchlessInsertionMarks = {
  Start: string;
  EditDocument: string;
  ScreenUpdate: string;
};

type StagedInsertionMarks = FetchlessInsertionMarks & {
  FetchData: string;
};

type FetchlessInsertionMeasures = {
  TriggerToEdit: InsertionMeasure;
  EditToScreen: InsertionMeasure;
  Total: InsertionMeasure;
};

type StagedInsertionMeasures = {
  TriggerToFetch: InsertionMeasure;
  FetchToEdit: InsertionMeasure;
  EditToScreen: InsertionMeasure;
  Total: InsertionMeasure;
};

type FetchlessInsertionMetrics<TMetadata> = {
  start: (insertionStart: InsertionStart) => void;
  markEditDocument: (insertionContext: InsertionContext<TMetadata>) => void;
  markScreenUpdate: (insertionContext: InsertionContext<TMetadata>) => void;
  cancel: (insertionStart: InsertionStart) => void;
};

type StagedInsertionMetrics<TMetadata> = FetchlessInsertionMetrics<TMetadata> & {
  markFetchData: (insertionContext: InsertionContext<TMetadata>) => void;
};

/**
 * Shared machinery for every insertable type. Marks are keyed by insertion id, so two
 * in-flight insertions produce two independent sets of measures rather than colliding on
 * one mark name.
 *
 * The mark-lifetime rule lives here: Start feeds several measures, so no individual
 * measure may clear its own marks. Teardown is explicit and runs once, after the last
 * measure for an id has been emitted.
 */
function createInsertionMetricsCore<TMetadata>({
  insertionMarks,
  legacyMeasure,
}: {
  insertionMarks: FetchlessInsertionMarks & { FetchData?: string };
  legacyMeasure?: InsertionMeasure;
}) {
  const markNames = [
    insertionMarks.Start,
    insertionMarks.FetchData,
    insertionMarks.EditDocument,
    insertionMarks.ScreenUpdate,
  ].filter((markName): markName is string => markName != null);

  const clearMarks = (insertionId: string): void => {
    markNames.forEach((markName) => {
      performance.clearMarks(createMarkForId(markName, insertionId));
    });
  };

  const hasMark = (markName: string, insertionId: string): boolean =>
    performance.hasEntry(createMarkForId(markName, insertionId));

  /** Emits a measure only when both of its boundary marks exist for this insertion. */
  const measureStage = (
    insertionContext: InsertionContext<TMetadata>,
    measure: InsertionMeasure
  ): void => {
    const { insertionId, ...metadata } = insertionContext;
    const startMark = createMarkForId(measure.startMark, insertionId);
    const endMark = createMarkForId(measure.endMark, insertionId);
    if (!performance.hasEntry(startMark) || !performance.hasEntry(endMark)) return;

    performance.measure(measure.name, {
      start: startMark,
      end: endMark,
      detail: metadata,
    });
  };

  return {
    start: ({ insertionId }: InsertionStart): void => {
      clearMarks(insertionId);
      performance.mark(insertionMarks.Start, insertionId);
    },

    markStage: (
      insertionContext: InsertionContext<TMetadata>,
      markName: string,
      stageMeasure: InsertionMeasure
    ): void => {
      const { insertionId } = insertionContext;
      if (!hasMark(insertionMarks.Start, insertionId)) return;

      performance.mark(markName, insertionId);
      measureStage(insertionContext, stageMeasure);

      if (legacyMeasure == null || markName !== insertionMarks.EditDocument) return;
      // Emitted without detail so it keeps reaching the analytics observer by its existing
      // out-of-band route rather than by measure detail.
      performance.measure(
        legacyMeasure.name,
        createMarkForId(legacyMeasure.startMark, insertionId),
        createMarkForId(legacyMeasure.endMark, insertionId)
      );
    },

    finish: (
      insertionContext: InsertionContext<TMetadata>,
      editToScreen: InsertionMeasure,
      total: InsertionMeasure
    ): void => {
      const { insertionId } = insertionContext;
      if (!hasMark(insertionMarks.EditDocument, insertionId)) return;

      try {
        performance.mark(insertionMarks.ScreenUpdate, insertionId);
        measureStage(insertionContext, editToScreen);
        measureStage(insertionContext, total);
      } finally {
        clearMarks(insertionId);
      }
    },

    cancel: ({ insertionId }: InsertionStart): void => {
      clearMarks(insertionId);
    },
  };
}

/**
 * For insertable types whose content is fetched: templates and macros.
 *
 * `legacyMeasure` is how templates keep emitting their original `insertTemplate` event on
 * the unchanged trigger-to-transform boundary. Macros have no history to preserve, so they
 * pass none.
 */
export function withInsertionMetrics<TMetadata>({
  marks: insertionMarks,
  measures: insertionMeasures,
  legacyMeasure,
}: {
  marks: StagedInsertionMarks;
  measures: StagedInsertionMeasures;
  legacyMeasure?: InsertionMeasure;
}): StagedInsertionMetrics<TMetadata> {
  const core = createInsertionMetricsCore<TMetadata>({ insertionMarks, legacyMeasure });

  return {
    start: core.start,
    markFetchData: (insertionContext): void => {
      core.markStage(insertionContext, insertionMarks.FetchData, insertionMeasures.TriggerToFetch);
    },
    markEditDocument: (insertionContext): void => {
      core.markStage(insertionContext, insertionMarks.EditDocument, insertionMeasures.FetchToEdit);
    },
    markScreenUpdate: (insertionContext): void => {
      core.finish(insertionContext, insertionMeasures.EditToScreen, insertionMeasures.Total);
    },
    cancel: core.cancel,
  };
}

/**
 * For picklists, whose options are already resident on the client. There is no fetch
 * point, so the trigger-to-edit span replaces the two fetch-bounded stages rather than
 * emitting a structurally near-zero series beside two real ones.
 */
export function withFetchlessInsertionMetrics<TMetadata>({
  marks: insertionMarks,
  measures: insertionMeasures,
}: {
  marks: FetchlessInsertionMarks;
  measures: FetchlessInsertionMeasures;
}): FetchlessInsertionMetrics<TMetadata> {
  const core = createInsertionMetricsCore<TMetadata>({ insertionMarks });

  return {
    start: core.start,
    markEditDocument: (insertionContext): void => {
      core.markStage(
        insertionContext,
        insertionMarks.EditDocument,
        insertionMeasures.TriggerToEdit
      );
    },
    markScreenUpdate: (insertionContext): void => {
      core.finish(insertionContext, insertionMeasures.EditToScreen, insertionMeasures.Total);
    },
    cancel: core.cancel,
  };
}

export const templateInsertionMetrics = withInsertionMetrics<InsertTemplateStageMetadata>({
  marks: marks.sirona.InsertTemplate,
  measures: measures.sirona.InsertTemplateStages,
  legacyMeasure: measures.sirona.InsertTemplate,
});

export const measureStableTextPerformance = ({
  textDone,
  text,
  id,
}: {
  textDone: boolean;
  text: string;
  id: string;
}) => {
  if (textDone === true) {
    // final stable text
    performance.mark(marks.nvoq.Dictation.StableTextReceived, id);
    performance.measure(
      measures.nvoq.Dictation.TimeToLastStableText.name,
      createMarkForId(measures.nvoq.Dictation.TimeToLastStableText.startMark, id),
      createMarkForId(measures.nvoq.Dictation.TimeToLastStableText.endMark, id)
    );
  } else {
    // checking first stable text
    if (!performance.hasEntry(createMarkForId(marks.nvoq.Dictation.StableTextReceived, id))) {
      performance.mark(createMarkForId(marks.nvoq.Dictation.StableTextReceived, id));
      performance.measure(
        measures.nvoq.Dictation.TimeToFirstStableText.name,
        createMarkForId(measures.nvoq.Dictation.TimeToFirstStableText.startMark, id),
        createMarkForId(measures.nvoq.Dictation.TimeToFirstStableText.endMark, id)
      );
    }
    // checking first non-empty stable text
    if (!performance.hasEntry(createMarkForId(marks.nvoq.Dictation.FirstNonEmptyStableText, id))) {
      const count = wordCount(text);
      if (count > 0) {
        const startMark = createMarkForId(
          measures.nvoq.Dictation.HypothesisStableGap.startMark,
          id
        );
        const endMark = createMarkForId(measures.nvoq.Dictation.HypothesisStableGap.endMark, id);

        performance.mark(endMark);
        if (performance.hasEntry(startMark)) {
          performance.measure(measures.nvoq.Dictation.HypothesisStableGap.name, startMark, endMark);
        }
      }
    }
  }
};

export const measureHypothesisTextPerformance = ({ text, id }: { text: string; id: string }) => {
  // checking first non-empty hypothesis text
  if (
    !performance.hasEntry(createMarkForId(marks.nvoq.Dictation.FirstNonEmptyHypothesisText, id))
  ) {
    const count = wordCount(text);
    if (count > 0) {
      performance.mark(createMarkForId(marks.nvoq.Dictation.FirstNonEmptyHypothesisText, id));
    }
  }
};

export enum ReportNavigationMethod {
  // This is for inbuilt keyboard navigation (tab + shift tab)
  KeyboardNavigation = 'keyboard-navigation',
  // This is for a user's custom defined keyboard shortcuts
  KeyboardShortcut = 'keyboard-shortcut',
  // This is for controlling input devices (e.g. microphone buttons)
  ControlInputDevice = 'control-input-device',
  VoiceCommand = 'voice-command',
}

export enum ReportNavigationDirection {
  Forward = 'forward',
  Back = 'back',
}

export type ReportNavigationMetadata = {
  method: ReportNavigationMethod;
  direction: ReportNavigationDirection;
};

export const measureReportNavigationStart = () => {
  performance.mark(marks.sirona.ReportNavigation.Start);
};

export const measureReportNavigationEnd = (info?: ReportNavigationMetadata) => {
  performance.mark(marks.sirona.ReportNavigation.End);
  performance.measure(measures.sirona.ReportNavigation.name, {
    start: marks.sirona.ReportNavigation.Start,
    end: marks.sirona.ReportNavigation.End,
    detail: { reportNavigationMetadata: info },
  });
};

export enum AutoImpressionRenderMethod {
  Navigation = 'navigation',
  WandIconClick = 'wand-icon-click',
  VoiceCommand = 'voice-command',
  DictaphoneButtonAction = 'dictaphone-button-action',
  KeyboardShortcut = 'keyboard-shortcut',
}

export type AutoImpressionRenderMetadata = {
  triggerMethod: AutoImpressionRenderMethod;
};

export enum ReportExitAction {
  Sign = 'sign',
  BypassSign = 'bypass-sign',
  Draft = 'draft',
  Discard = 'discard',
  Prelim = 'prelim',
  BypassPrelim = 'bypass-prelim',
  MoveToQC = 'move-to-qc',
}

export enum ReportExitActionInputMethod {
  Button = 'ui-button',
  Keyboard = 'keyboard-shortcut',
  VoiceCommand = 'voice-command',
  ControlInputDevice = 'control-input-device',
}

export type ReportExitActionMetadata = {
  exitAction: ReportExitAction;
  inputMethod: ReportExitActionInputMethod;
  hasConfirmationDialogTriggered: boolean;
};

export const reportExitActionDataState: RecoilState<ReportExitActionMetadata | null> =
  atom<ReportExitActionMetadata | null>({
    key: 'reportExitActionDataState',
    default: null,
  });

export const measureReportExitActionStart = () => {
  performance.mark(marks.sirona.ReportExitAction.Start);
};

export const measureReportExitActionEnd = (info?: ReportExitActionMetadata) => {
  performance.mark(marks.sirona.ReportExitAction.End);
  performance.measure(measures.sirona.ReportExitAction.name, {
    start: marks.sirona.ReportExitAction.Start,
    end: marks.sirona.ReportExitAction.End,
    detail: { reportExitActionMetadata: info },
  });
};

export const measureAutoImpressionRenderStart = () => {
  performance.mark(marks.sirona.AutoImpressionRender.Start);
};

export const measureAutoImpressionRenderEnd = (info: AutoImpressionRenderMetadata) => {
  performance.mark(marks.sirona.AutoImpressionRender.End);
  performance.measure(measures.sirona.AutoImpressionRender.name, {
    start: marks.sirona.AutoImpressionRender.Start,
    end: marks.sirona.AutoImpressionRender.End,
    detail: { autoImpressionRenderMetadata: info },
  });
};

type AutoloadNextCaseMetadata = {
  exitAction: ReportExitAction;
  preClaimed: boolean;
};

export const measureAutoloadNextCaseStart = () => {
  performance.mark(marks.sirona.AutoloadNextCase.Start);
};

export const measureAutoloadNextCaseEnd = (info?: AutoloadNextCaseMetadata) => {
  performance.mark(marks.sirona.AutoloadNextCase.End);
  performance.measure(measures.sirona.AutoloadNextCase.name, {
    start: marks.sirona.AutoloadNextCase.Start,
    end: marks.sirona.AutoloadNextCase.End,
    detail: { autoloadNextCaseMetadata: info },
  });
};
